import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search, DollarSign, Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { money, fechaMX } from "@/lib/format";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { useLocation, useNavigate } from "react-router-dom";
import ReporteModulo from "@/components/ReporteModulo";
import AgrupadoPorFecha from "@/components/AgrupadoPorFecha";
import { useVendedoresActivos } from "@/hooks/useVendedoresActivos";

const estBadge = (e) => (e === "Liquidado" ? "bg-emerald-500" : "bg-amber-500");
const grupoFecha = (c) => c.pedido?.fecha_pago || c.fecha_pago || (c.created_at ? String(c.created_at).slice(0, 10) : null);
// Si ya se pagó al vendedor (Liquidado) manda la fecha de liquidación; si no, la del pedido.
const efFecha = (c) => (c.estatus === "Liquidado" && c.fecha_liquidacion) ? c.fecha_liquidacion : grupoFecha(c);
const vendNombre = (c) => c.vendedor?.full_name;
const sumCom = (arr) => arr.reduce((s, c) => s + Number(c.total_comisiones || 0), 0);
const PAGO_OPTS = [
  { value: "pagadas", label: "Comisiones pagadas", match: (c) => c.estatus === "Liquidado" },
  { value: "pendientes", label: "Comisiones por pagar", match: (c) => c.estatus !== "Liquidado" },
  { value: "disponibles", label: "Comisiones disponibles para pagar", match: (c) => c.estatus !== "Liquidado" && c.pedido_pagado },
];

export default function Comisiones() {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ tipo: "", metodo_calculo: "porcentaje", porcentaje: "", monto: "", comentario: "", catalogoId: "" });
  const [catalogo, setCatalogo] = useState([]);
  const [editCp, setEditCp] = useState(null); // { id, metodo_calculo }
  const [editVal, setEditVal] = useState("");
  const [totVend, setTotVend] = useState("todos");
  const [totPago, setTotPago] = useState("todas"); // todas | pagadas | pendientes

  const fetch = async () => {
    try { setLoading(true); const { data } = await api.get("/comisiones"); setRows(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);
  useRefetchOnFocus(fetch);
  useEffect(() => { api.get("/catalogo-comisiones").then(({ data }) => setCatalogo(data || [])).catch(() => {}); }, []);

  // Auto-abrir la comisión de un pedido cuando se entra desde Historial de pedidos.
  useEffect(() => {
    const pid = location.state?.comisionPedidoId;
    if (!pid || !rows.length) return;
    const com = rows.find((c) => c.pedido_id === pid);
    if (com) setSel(com);
    else toast.error("Este pedido no tiene una comisión asociada.");
    navigate(location.pathname, { replace: true, state: {} });
  }, [rows, location.state]);

  const pickCatalogo = (id) => {
    const item = catalogo.find((x) => x.id === id);
    if (!item) return;
    setForm((f) => ({
      ...f,
      catalogoId: id,
      tipo: item.nombre,
      metodo_calculo: item.metodo_calculo,
      porcentaje: item.metodo_calculo === "porcentaje" ? String(item.valor) : "",
      monto: item.metodo_calculo === "monto_fijo" ? String(item.valor) : "",
    }));
  };

  const refreshSel = async () => {
    const { data } = await api.get("/comisiones");
    setRows(data || []);
    if (sel) setSel(data.find((c) => c.id === sel.id) || null);
  };

  const cambiarEstatus = async (id, estatus, facturaRecibida) => {
    if (estatus === "Liquidado" && !facturaRecibida) {
      toast.error("No se puede liquidar hasta marcar la factura del vendedor como recibida.");
      return;
    }
    try { await api.patch(`/comisiones/${id}/estatus`, { estatus }); toast.success("Estatus actualizado"); refreshSel(); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const setFacturaVendedor = async (id, recibida, fecha) => {
    try {
      await api.patch(`/comisiones/${id}/factura-vendedor`, { recibida, fecha: recibida ? fecha : null });
      toast.success("Factura del vendedor actualizada");
      refreshSel();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const hoyISO = () => new Date().toISOString().slice(0, 10);
  const addConcepto = async () => {
    if (!form.tipo) return toast.error("Indique el tipo/concepto");
    const liquidada = sel?.estatus === "Liquidado";
    if (liquidada && !window.confirm("Esta comisión ya está LIQUIDADA (ya se pagó al vendedor). ¿Deseas corregirla de todas formas? Quedará registrado el cambio.")) return;
    try {
      await api.post(`/comisiones/${sel.id}/conceptos`, {
        tipo: form.tipo, metodo_calculo: form.metodo_calculo,
        porcentaje: form.metodo_calculo === "porcentaje" ? Number(form.porcentaje || 0) : 0,
        monto: form.metodo_calculo === "monto_fijo" ? Number(form.monto || 0) : null, comentario: form.comentario,
        override: liquidada,
      });
      toast.success("Concepto agregado");
      setForm({ tipo: "", metodo_calculo: "porcentaje", porcentaje: "", monto: "", comentario: "", catalogoId: "" });
      refreshSel();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const delConcepto = async (cid) => {
    if (sel?.estatus === "Liquidado" && !window.confirm("Esta comisión ya está LIQUIDADA. ¿Eliminar este concepto de todas formas?")) return;
    try { await api.delete(`/comisiones/${sel.id}/conceptos/${cid}`); toast.success("Concepto eliminado"); refreshSel(); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const startEditCp = (cp) => { setEditCp({ id: cp.id, metodo_calculo: cp.metodo_calculo }); setEditVal(String(cp.metodo_calculo === "porcentaje" ? (cp.porcentaje ?? "") : (cp.monto ?? ""))); };
  const cancelEditCp = () => { setEditCp(null); setEditVal(""); };
  const saveEditCp = async () => {
    if (!editCp) return;
    const liquidada = sel?.estatus === "Liquidado";
    if (liquidada && !window.confirm("Esta comisión ya está LIQUIDADA (ya se pagó al vendedor). ¿Deseas corregir este concepto de todas formas?")) return;
    const body = editCp.metodo_calculo === "porcentaje" ? { porcentaje: Number(editVal || 0), override: liquidada } : { monto: Number(editVal || 0), override: liquidada };
    try {
      await api.patch(`/comisiones/${sel.id}/conceptos/${editCp.id}`, body);
      toast.success("Concepto actualizado");
      cancelEditCp(); refreshSel();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };


  const vendedores = useVendedoresActivos(isAdmin, rows, vendNombre);

  const base = useMemo(() => rows.filter((c) => {
    if (search && !`${c.numero_pedido} ${c.cliente} ${c.estatus} ${c.vendedor?.full_name || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (isAdmin && totVend !== "todos" && (c.vendedor?.full_name || "") !== totVend) return false;
    const opt = PAGO_OPTS.find((o) => o.value === totPago);
    return !opt || opt.match(c);
  }), [rows, search, totVend, totPago, isAdmin]);
  const totalLabel = totPago === "pendientes" ? "Total por pagar" : totPago === "pagadas" ? "Total pagado" : totPago === "disponibles" ? "Total disponible para pagar" : "Total";


  const renderRow = (c) => (
    <TableRow key={c.id}>
      <TableCell className="font-mono text-xs font-semibold">{c.numero_pedido}</TableCell>
      <TableCell className="font-medium">{c.cliente}</TableCell>
      <TableCell>{c.vendedor?.full_name || "-"}</TableCell>
      <TableCell className="text-right">{money(c.monto_venta)}</TableCell>
      <TableCell className="text-right font-semibold">{money(c.total_comisiones)}</TableCell>
      <TableCell data-testid={`factura-vendedor-${c.id}`}>
        {!c.pedido_pagado ? (
          <span className="text-xs text-muted-foreground italic">Disponible al pagar</span>
        ) : isAdmin ? (
          <div className="flex items-center gap-2">
            <Select value={c.factura_vendedor_recibida ? "si" : "no"} onValueChange={(v) => setFacturaVendedor(c.id, v === "si", c.factura_vendedor_fecha || hoyISO())}>
              <SelectTrigger className="h-8 w-36" data-testid={`factura-vendedor-select-${c.id}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="no">Sin factura del vendedor</SelectItem><SelectItem value="si">Facturado</SelectItem></SelectContent>
            </Select>
            {c.factura_vendedor_recibida && (
              <Input type="date" className="h-8 w-40" value={c.factura_vendedor_fecha || ""} onChange={(e) => setFacturaVendedor(c.id, true, e.target.value)} data-testid={`factura-vendedor-fecha-${c.id}`} />
            )}
          </div>
        ) : (
          c.factura_vendedor_recibida
            ? <Badge className="bg-emerald-500">Facturado{c.factura_vendedor_fecha ? ` · ${fechaMX(c.factura_vendedor_fecha)}` : ""}</Badge>
            : <Badge className="bg-amber-500">Sin factura del vendedor</Badge>
        )}
      </TableCell>
      <TableCell data-testid={`estatus-cell-${c.id}`}>
        {isAdmin ? (
          <Select value={c.estatus} onValueChange={(v) => cambiarEstatus(c.id, v, c.factura_vendedor_recibida)}>
            <SelectTrigger className="h-8 w-44" data-testid={`estatus-select-${c.id}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Pendiente de liquidar">Pendiente de liquidar</SelectItem><SelectItem value="Liquidado" disabled={!c.factura_vendedor_recibida}>Liquidado</SelectItem></SelectContent>
          </Select>
        ) : (
          <Badge className={estBadge(c.estatus)}>{c.estatus}</Badge>
        )}
      </TableCell>
      <TableCell className="text-sm" data-testid={`fecha-liquidacion-${c.id}`}>{c.estatus === "Liquidado" && c.fecha_liquidacion ? fechaMX(c.fecha_liquidacion) : "-"}</TableCell>
      <TableCell className="text-right"><Button variant="outline" size="icon" title="Gestionar" onClick={() => setSel(c)} data-testid={`comision-gestionar-${c.id}`}><DollarSign className="h-4 w-4" /></Button></TableCell>
    </TableRow>
  );

  const renderTabla = (list) => (
    <div className="border rounded-md overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Pedido</TableHead><TableHead>Cliente</TableHead><TableHead>Vendedor</TableHead><TableHead className="text-right">Monto Venta</TableHead><TableHead className="text-right">Comisión</TableHead><TableHead>Factura vendedor</TableHead><TableHead>Estatus</TableHead><TableHead>Fecha liquidación</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
        <TableBody>
          {list.map(renderRow)}
          <TableRow className="bg-muted/40 font-semibold">
            <TableCell colSpan={4} className="text-right">{totalLabel}</TableCell>
            <TableCell className="text-right" data-testid="seccion-total">{money(sumCom(list))}</TableCell>
            <TableCell colSpan={4} className="text-sm text-muted-foreground">{list.length} registro(s)</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="comisiones-page">
      <div><h1 className="text-3xl font-bold tracking-tight">Comisiones</h1><p className="text-muted-foreground mt-2">Liquidación de comisiones por pedido.</p></div>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Buscar por pedido, cliente, vendedor o estatus..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="comision-search" />
            </div>
            <ReporteModulo
              titulo="REPORTE DE COMISIONES"
              filenameBase="Reporte de comisiones"
              prefix="rep-com"
              triggerTestId="comision-reporte-btn"
              buttonVariant="outline"
              rows={base}
              getFecha={efFecha}
              groupBy={isAdmin ? (c) => c.vendedor?.full_name || "Sin vendedor" : undefined}
              filtros={[
                { label: "Vendedor", value: isAdmin && totVend !== "todos" ? totVend : "" },
                { label: "Pago al vendedor", value: PAGO_OPTS.find((o) => o.value === totPago)?.label || "" },
                { label: "Búsqueda", value: search },
              ]}
              columns={[
                { header: "Pedido", value: (c) => c.numero_pedido, mono: true },
                { header: "Cliente", value: (c) => c.cliente },
                { header: "Vendedor", value: (c) => c.vendedor?.full_name || "-" },
                { header: "Monto Venta", align: "right", value: (c) => money(c.monto_venta) },
                { header: "Comisión", align: "right", value: (c) => money(c.total_comisiones) },
                { header: "Desglose", value: (c) => (c.comision_conceptos || []).length === 0 ? "—" : (
                  <div className="space-y-0.5">{c.comision_conceptos.map((cp) => <div key={cp.id}>{cp.tipo}{cp.metodo_calculo === "porcentaje" ? ` (${cp.porcentaje}%)` : ""}: {money(cp.monto)}</div>)}</div>
                ) },
                { header: "Estatus", value: (c) => (c.estatus === "Liquidado" ? "Liquidado" : "Pendiente") },
                { header: "Fecha liq.", value: (c) => (c.estatus === "Liquidado" && c.fecha_liquidacion ? fechaMX(c.fecha_liquidacion) : "-") },
              ]}
              subtotal={(list) => money(sumCom(list))}
              totales={(list) => [
                { label: "Total comisiones", value: money(sumCom(list)) },
                { label: "Total liquidado", value: money(sumCom(list.filter((c) => c.estatus === "Liquidado"))) },
                { label: "Total pendiente", value: money(sumCom(list.filter((c) => c.estatus !== "Liquidado"))) },
              ]}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <p className="text-center text-muted-foreground py-12">Cargando...</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2" data-testid="comisiones-filtros">
                {isAdmin && (
                  <Select value={totVend} onValueChange={setTotVend}>
                    <SelectTrigger className="h-9 w-52" data-testid="tot-vendedor"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos los vendedores</SelectItem>
                      {vendedores.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Select value={totPago} onValueChange={setTotPago}>
                  <SelectTrigger className="h-9 w-60" data-testid="tot-pago"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    {PAGO_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <AgrupadoPorFecha
                rows={base}
                getFecha={efFecha}
                testidPrefix="comision"
                emptyText="No hay comisiones en este periodo."
                resumen={(list) => <>{totalLabel}: <span className="font-semibold text-foreground">{money(sumCom(list))}</span></>}
                renderRows={renderTabla}
              />
            </>
          )}
        </CardContent>
      </Card>


      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Comisión · {sel?.numero_pedido}</DialogTitle><DialogDescription>{sel?.cliente} · Monto venta: {money(sel?.monto_venta)}</DialogDescription></DialogHeader>
          {sel && (
            <div className="space-y-5">
              {isAdmin && (
                <div className="flex items-center gap-3">
                  <Label>Estatus:</Label>
                  <Select value={sel.estatus} onValueChange={(v) => cambiarEstatus(sel.id, v, sel.factura_vendedor_recibida)}>
                    <SelectTrigger className="w-56" data-testid="comision-estatus"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Pendiente de liquidar">Pendiente de liquidar</SelectItem><SelectItem value="Liquidado" disabled={!sel.factura_vendedor_recibida}>Liquidado</SelectItem></SelectContent>
                  </Select>
                  {!sel.factura_vendedor_recibida && <span className="text-xs text-muted-foreground italic" data-testid="liquidar-bloqueado-hint">Marca la factura del vendedor como recibida para poder liquidar.</span>}
                </div>
              )}

              <div className="border rounded-md">
                <Table>
                  <TableHeader><TableRow><TableHead>Concepto</TableHead><TableHead>Método</TableHead><TableHead className="text-right">%</TableHead><TableHead className="text-right">Monto</TableHead>{isAdmin && <TableHead className="w-12"></TableHead>}</TableRow></TableHeader>
                  <TableBody>
                    {(sel.comision_conceptos || []).length === 0 ? <TableRow><TableCell colSpan={isAdmin ? 5 : 4} className="text-center h-16 text-muted-foreground">Sin conceptos.</TableCell></TableRow>
                      : sel.comision_conceptos.map((cp) => (
                        <TableRow key={cp.id}>
                          <TableCell className="font-medium">{cp.tipo}</TableCell>
                          <TableCell>{cp.metodo_calculo === "porcentaje" ? "Porcentaje" : "Monto fijo"}</TableCell>
                          <TableCell className="text-right">
                            {editCp?.id === cp.id && cp.metodo_calculo === "porcentaje"
                              ? <Input type="number" min="0" step="0.01" value={editVal} onChange={(e) => setEditVal(e.target.value)} className="h-8 w-24 ml-auto" data-testid={`edit-cp-input-${cp.id}`} autoFocus />
                              : (cp.metodo_calculo === "porcentaje" ? `${cp.porcentaje}%` : "-")}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {editCp?.id === cp.id && cp.metodo_calculo !== "porcentaje"
                              ? <Input type="number" min="0" step="0.01" value={editVal} onChange={(e) => setEditVal(e.target.value)} className="h-8 w-28 ml-auto" data-testid={`edit-cp-input-${cp.id}`} autoFocus />
                              : money(cp.monto)}
                          </TableCell>
                          {isAdmin && <TableCell>
                            <div className="flex gap-1">
                              {editCp?.id === cp.id ? (
                                <>
                                  <Button variant="ghost" size="icon" className="text-emerald-600" title="Guardar" onClick={saveEditCp} data-testid={`save-cp-${cp.id}`}><Check className="h-4 w-4" /></Button>
                                  <Button variant="ghost" size="icon" title="Cancelar" onClick={cancelEditCp} data-testid={`cancel-cp-${cp.id}`}><X className="h-4 w-4" /></Button>
                                </>
                              ) : (
                                <>
                                  <Button variant="ghost" size="icon" title="Editar" onClick={() => startEditCp(cp)} data-testid={`edit-cp-${cp.id}`}><Pencil className="h-4 w-4" /></Button>
                                  <Button variant="ghost" size="icon" className="text-destructive" title="Eliminar" onClick={() => delConcepto(cp.id)}><Trash2 className="h-4 w-4" /></Button>
                                </>
                              )}
                            </div>
                          </TableCell>}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-end font-bold">Total comisión: {money(sel.total_comisiones)}</div>

              {isAdmin && (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="font-semibold">Agregar comisión del catálogo</h4>
                  {sel.estatus === "Liquidado" && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="comision-liquidada-warning">
                      ⚠️ Esta comisión ya está <span className="font-semibold">Liquidada</span> (ya se pagó al vendedor). Puedes corregirla como Admin, pero el cambio afecta un pago ya registrado.
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">El catálogo se administra en Configuración del sistema › Comisiones.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2 md:col-span-2">
                      <Label>Concepto del catálogo</Label>
                      <Select value={form.catalogoId} onValueChange={pickCatalogo}>
                        <SelectTrigger data-testid="concepto-catalogo"><SelectValue placeholder={catalogo.length ? "Seleccione un concepto del catálogo" : "Sin conceptos en el catálogo"} /></SelectTrigger>
                        <SelectContent>
                          {catalogo.map((item) => (
                            <SelectItem key={item.id} value={item.id} data-testid={`concepto-catalogo-item-${item.id}`}>
                              {item.nombre} · {item.metodo_calculo === "porcentaje" ? `${item.valor}%` : money(item.valor)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Método</Label><Input value={form.catalogoId ? (form.metodo_calculo === "porcentaje" ? "Porcentaje sobre venta" : "Monto fijo") : ""} disabled data-testid="concepto-metodo" /></div>
                    {form.metodo_calculo === "monto_fijo"
                      ? <div className="space-y-2"><Label>Monto</Label><Input value={form.monto} disabled data-testid="concepto-monto" /></div>
                      : <div className="space-y-2"><Label>Porcentaje (%)</Label><Input value={form.porcentaje} disabled data-testid="concepto-porcentaje" /></div>}
                    <div className="space-y-2 md:col-span-2"><Label>Comentario (opcional)</Label><Input value={form.comentario} onChange={(e) => setForm({ ...form, comentario: e.target.value })} placeholder="Nota interna" /></div>
                  </div>
                  {form.catalogoId && (
                    <p className="text-sm">Comisión a aplicar sobre {money(sel.monto_venta)}: <span className="font-semibold text-emerald-600" data-testid="concepto-preview">{money(form.metodo_calculo === "porcentaje" ? (Number(sel.monto_venta || 0) * Number(form.porcentaje || 0) / 100) : Number(form.monto || 0))}</span></p>
                  )}
                  <Button onClick={addConcepto} disabled={!form.catalogoId} data-testid="concepto-add"><Plus className="h-4 w-4 mr-2" /> Agregar comisión</Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSel(null)} data-testid="comision-cerrar">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
